import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { Schema } from './ContactForm.schema';

import { Section } from '../../snippets';

const ROLE_OPTIONS = ['Brand', 'Agency', 'Tech Partner', 'Other'];
const RECAPTCHA_SITE_KEY = '6Ld32v8qAAAAAEgfFdTBJbfu4-YUuh5QUNRBeUoB';

export function ContactForm({ cms }) {
  const {
    heading,
    description,
    image,
    imageAlt,
    enableMaxWidth,
    buttonText,
    successHeading,
    successMessage,
  } = cms;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    storefrontUrl: '',
    message: '',
    roles: [],
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleRoleToggle(role) {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading || !captchaValue) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'marketing_contact_form',
          pageName: heading || 'Contact Form',
          pageUri: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      const respJson = await resp.json();
      if (!respJson.success) throw new Error('Could not subscribe');
      setSuccess(true);
    } catch (err) {
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  const inputClasses =
    'w-full rounded-lg border border-white/20 bg-transparent px-3 py-2.5 font-inter text-base text-white placeholder:text-grey-400 outline-none focus:border-brand';

  return (
    <Section cms={cms}>
      <div
        className={`grid md:grid-cols-3 lg:grid-cols-2 ${
          enableMaxWidth ? 'mx-auto max-w-7xl px-4' : ''
        }`}
      >
        <div
          className={`md:col-span-2 lg:col-span-1 ${
            enableMaxWidth ? 'md:pr-4 lg:pr-24' : 'p-4 lg:p-24'
          }`}
        >
          {heading ? <h1 className="mb-5">{heading}</h1> : null}
          {description ? <p>{description}</p> : null}
          <div className="py-12">
            {success ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-[#00261C]/10 bg-[#00261C]/5 px-6 py-10 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-[#00261C]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-xl font-semibold">
                  {successHeading || "You're all set!"}
                </h3>
                <p className="text-sm opacity-70">
                  {successMessage ||
                    'We received your request and will be in touch shortly.'}
                </p>
              </div>
            ) : (
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                {/* Row 1: First name / Last name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-white">
                      First name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className={inputClasses}
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-white">
                      Last name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className={inputClasses}
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Row 2: Email */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={inputClasses}
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Row 3: Company */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white">
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    className={inputClasses}
                    placeholder="Company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Row 4: Storefront URL */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white">
                    Storefront URL *
                  </label>
                  <input
                    type="text"
                    name="storefrontUrl"
                    className={inputClasses}
                    placeholder="your-store.com"
                    value={formData.storefrontUrl}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Row 5: Message */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-white">
                    How can we help you?
                  </label>
                  <textarea
                    name="message"
                    className={`${inputClasses} h-32 resize-none`}
                    placeholder="Tell us about your project..."
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                {/* Row 6: Role checkboxes */}
                <div>
                  <p className="mb-1.5 text-sm font-medium text-white">
                    I am a...
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {ROLE_OPTIONS.map((role) => (
                      <label
                        key={role}
                        className="flex cursor-pointer items-center gap-2 text-sm text-white"
                      >
                        <input
                          type="checkbox"
                          className="accent-brand"
                          checked={formData.roles.includes(role)}
                          onChange={() => handleRoleToggle(role)}
                        />
                        {role}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Row 7: reCAPTCHA */}
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="dark"
                  onChange={setCaptchaValue}
                />

                {/* Row 8: Submit */}
                <button
                  type="submit"
                  className="btn-primary w-full text-white"
                  disabled={loading || !captchaValue}
                >
                  {loading ? 'Submitting...' : buttonText || 'Book a Call'}
                </button>
              </form>
            )}
          </div>
        </div>
        {image ? (
          <div className="flex">
            <img
              className="h-full w-full object-cover object-left"
              src={image.src}
              alt={imageAlt}
            />
          </div>
        ) : null}
      </div>
    </Section>
  );
}

ContactForm.displayName = 'ContactForm';
ContactForm.Schema = Schema;
